import React, { useState, useEffect } from 'react';
import { MainNav } from "@/components/MainNav";
import { DealersContainer } from "@/components/dealers/DealersContainer";
import { DealersSEO } from "@/components/dealers/DealersSEO";
import { useHydratedDealersPage } from "@/hooks/dealers/useHydratedDealersPage";
import { useSEOStats } from "@/hooks/dealers/useSEOStats";
import { ServerDealersContent } from "@/components/dealers/ServerDealersContent";
import { isSSR } from "@/lib/isSSR";
import { Skeleton } from "@/components/ui/skeleton";

interface DealersProps {
  buySpread?: number;
  className?: string;
  serverDealers?: any[];
  serverCities?: string[];
  // SEO data pre-loaded from SSR
  seoStats?: {
    nationalStats?: any;
    topCities?: Array<{city: string, count: number}>;
  };
}

export function Dealers({ className, seoStats }: DealersProps) {
  // Stato per forzare loading iniziale e prevenire flash di errore
  const [initialLoading, setInitialLoading] = useState(true);
  
  const {
    dealers,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    enableInfiniteScroll,
    filters,
    view,
    cities,
    updateFilters,
    setView,
    handleCardClick,
    handleRefresh,
    handleCityClick,
    loadMore
  } = useHydratedDealersPage();

  // Timer per nascondere loading iniziale dopo un breve periodo
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 800); // 800ms per garantire transizione smooth

    return () => clearTimeout(timer);
  }, []);

  // Calcola stato di loading finale - mostra sempre loading inizialmente
  const showLoading = initialLoading || isLoading;

  // Fetch SEO stats for national page with pre-loaded data
  const { stats, topCities } = useSEOStats({ 
    enabled: !isSSR(),
    initialStats: seoStats?.nationalStats,
    initialTopCities: seoStats?.topCities
  });

  // Convert cities array to topCities format for ServerDealersContent
  const formattedTopCities = topCities || (cities ? cities.map((city, index) => ({
    city: city,
    count: Math.max(1, Math.floor(Math.random() * 10) + 1) // Placeholder count
  })) : []);

  return (
    <>
      <DealersSEO 
        dealers={dealers} 
        nationalStats={stats}
        topCities={topCities}
      />
      <MainNav />
      
      <div className="container mx-auto px-4 py-6 sm:px-6">
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Compro Oro
          </h1>
          <p className="text-muted-foreground">
            Trova il miglior compro oro vicino a te
            {enableInfiniteScroll && totalCount > 0 && (
              <span className="block text-sm mt-1">
                {totalCount.toLocaleString()} compro oro disponibili
              </span>
            )}
          </p>
        </div>
        
        {isSSR() ? (
          <ServerDealersContent 
            dealers={dealers} 
            nationalStats={stats}
            topCities={formattedTopCities}
          />
        ) : showLoading ? (
          <div className="space-y-6 w-full">
            <div className="bg-card rounded-lg shadow-sm p-4 mb-4 w-full">
              <Skeleton className="h-12 w-full max-w-[300px]" />
            </div>
            <div className="bg-card rounded-lg shadow-sm p-4 w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="space-y-3">
                    <Skeleton className="h-48 w-full rounded-lg" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <DealersContainer
            dealers={dealers}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            totalCount={totalCount}
            enableInfiniteScroll={enableInfiniteScroll}
            filters={filters}
            view={view}
            onViewChange={setView}
            onUpdateFilters={updateFilters}
            onDealerClick={handleCardClick}
            onRefresh={handleRefresh}
            onLoadMore={loadMore}
            className={className}
            cities={cities}
            onCityClick={handleCityClick}
          />
        )}
      </div>
    </>
  );
}

export default Dealers;
